import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit2, Trash2, Save } from "lucide-react";
import { useRoleSettingsData, Role } from "@/lib/settings/rolesData";
import { CharLimitMessage } from "@/components/common/CharLimitMessage";
import { ROLE_LIMITS } from "@/lib/settings/settingsLimits";
import { PermissionCategory } from "../../enum/PermissionCategory";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

interface RolesSettingsTabProps {
  onAddRole?: () => void;
  onEditRole?: (role: Role) => void;
  onDeleteRole?: (roleId: string) => void;
}

const isSystemDefinedRole = (role?: Role | null) =>
  role?.isSytemPreDefined === true ||
  role?.isSytemPreDefined === "true" ||
  role?.isSytemPreDefined === 1 ||
  role?.isSystemPreDefined === true ||
  role?.isSystemPreDefined === "true" ||
  role?.isSystemPreDefined === 1;

export function RolesSettingsTab({
  onAddRole,
  onEditRole,
  onDeleteRole,
}: RolesSettingsTabProps) {
  const { teamPermissions, roles, userData, deleteRoleSettings } =
    useRoleSettingsData();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const getPermissionCount = (permissions = []) => {
    return permissions.filter((p) => p.isPermissionEnabled).length;
  };

  const handleDeleteRole = (roleId: string) => {
    if (!roleId) return;

    setDeleteError(null);
    setSelectedRoleId(roleId);
    setDeleteModalOpen(true);
  };

  const confirmDeleteRole = async () => {
    if (!selectedRoleId) return;

    try {
      const response = await deleteRoleSettings(selectedRoleId);

      if (response?.isInUse) {
        setDeleteError(
          `This role is assigned to ${response.assignedUserCount} user(s). Remove them first before deleting.`,
        );
      } else {
        setDeleteModalOpen(false);
        setSelectedRoleId(null);
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          Roles & Permissions
        </h2>
        <Button
          size="sm"
          className="bg-slate-900 hover:bg-slate-800"
          onClick={onAddRole}
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Role
        </Button>
      </div>

      <div className="space-y-4">
        {roles.map((role) => (
          <div key={role.id} className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900 mb-1">{role.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{role.description}</p>
                <div className="text-xs text-gray-500">
                  {getPermissionCount(role?.permissions)} permissions assigned
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEditRole(role)}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>

                {!isSystemDefinedRole(role) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteRole(role.id)}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {deleteError ? "Cannot Delete Role" : "Delete Role"}
            </DialogTitle>
            <DialogDescription>
              {deleteError
                ? deleteError
                : "Are you sure you want to delete this role? This action cannot be undone."}
            </DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>

            {!deleteError && (
              <Button variant="default" onClick={confirmDeleteRole}>
                Delete
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface RoleFormPanelProps {
  role?: any;
  onSave: (roleData: any) => void;
  onBack: () => void;
}

export function RoleFormPanel({ role, onSave, onBack }: RoleFormPanelProps) {
  const { teamPermissions, roles, updateRoleSettings, saveRoleSettings } =
    useRoleSettingsData();
  const [showUpdateInfoModal, setShowUpdateInfoModal] = useState(false);
  const [saveSuccessTitle, setSaveSuccessTitle] = useState("Role Updated");
  const [saveSuccessMessage, setSaveSuccessMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState("");
  const isEditingSystemRole = isSystemDefinedRole(role);
  // Normalize teamPermissions for toggles
  const normalizedTeamPermissions =
    teamPermissions?.map((perm: any) => {
      const permId = perm._id ?? perm._id?.$oid ?? "";

      // Check if this permission exists in the current role
      const rolePerm = role?.permissions?.find((p: any) => p.id === permId);

      return {
        id: permId,
        label: perm.permissionKey ?? "",
        description: perm.permissionDescription ?? "",
        permissionName: perm.permissionName ?? "",
        category: perm.category ?? "General",
        action: perm.action,
        isPermissionEnabled: !!rolePerm, // ON only if role has this permission
      };
    }) ?? [];

  // Group by category using enum
  const permissionsByCategory: Record<
    string,
    (typeof normalizedTeamPermissions)[0][]
  > = normalizedTeamPermissions.reduce(
    (acc, perm) => {
      // Map numeric category to enum string
      const categoryName =
        PermissionCategory[perm.category as keyof typeof PermissionCategory] ??
        "General";

      if (!acc[categoryName]) acc[categoryName] = [];
      acc[categoryName].push(perm);
      return acc;
    },
    {} as Record<string, (typeof normalizedTeamPermissions)[0][]>,
  );

  const [formData, setFormData] = useState({
    name: role?.name || "",
    description: role?.description || "",
    systemRoleId: role?.systemRoleId || "",
    permissions: normalizedTeamPermissions,
  });

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setNameError("Role name is required");
      return;
    }

    const roleNameExists = roles.some(
      (r) =>
        r.name.toLowerCase().trim() === formData.name.toLowerCase().trim() &&
        r.id !== role?.id,
    );

    if (roleNameExists) {
      setNameError("Role name already exists");
      return;
    }

    setNameError("");

    try {
      setIsSaving(true);

      if (role?.id) {
        await updateRoleSettings({ ...formData, id: role.id });
        setSaveSuccessTitle("Role Updated");
        setSaveSuccessMessage(
          "Role permissions were updated successfully. The new changes will apply when affected users sign in again.",
        );
        setShowUpdateInfoModal(true);
      } else {
        await saveRoleSettings(formData);
        onBack();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const getPermissionCount = () => {
    return formData.permissions.filter((p) => p.isPermissionEnabled).length;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4 pb-4 border-b border-gray-200">
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Back
        </Button>
        <h2 className="text-lg font-semibold text-gray-900">
          {role ? "Edit Role" : "Create New Role"}
        </h2>
      </div>

      <div className="space-y-6">
        {/* Basic Information */}
        <div>
          <h3 className="font-medium text-gray-900 mb-4">Basic Information</h3>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="role-name">Role Name</Label>
              <Input
                id="role-name"
                value={formData.name}
                onChange={(e) => {
                  updateField("name", e.target.value);
                  setNameError("");
                }}
                placeholder="Enter role name (e.g., Pharmacist, Manager)"
                maxLength={ROLE_LIMITS.name}
              />
              <CharLimitMessage value={formData.name} max={ROLE_LIMITS.name} />
              {nameError && <p className="text-xs text-red-500">{nameError}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="role-description">Description</Label>
              <Textarea
                id="role-description"
                value={formData.description}
                onChange={(e) => updateField("description", e.target.value)}
                placeholder="Describe what this role can do"
                rows={3}
                maxLength={ROLE_LIMITS.description}
              />
              <CharLimitMessage
                value={formData.description}
                max={ROLE_LIMITS.description}
              />
            </div>
          </div>
        </div>

        {/* Permissions */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-gray-900">Permissions</h3>
            <Badge variant="secondary" className="text-xs">
              {getPermissionCount()} permissions selected
            </Badge>
          </div>

          <div className="space-y-4">
            {Object.entries(permissionsByCategory).map(([category, perms]) => (
              <div key={category} className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  {category}
                </h4>
                <div className="space-y-2">
                  {(perms || []).map((permission) => {
                    // Get the live state from formData.permissions
                    const rolePermission = formData.permissions.find(
                      (p) => p.id === permission.id,
                    );
                    const isEnabled =
                      rolePermission?.isPermissionEnabled ?? false;

                    return (
                      <div
                        key={permission.id || permission.label}
                        className="flex items-center justify-between border border-gray-200 rounded-lg p-4"
                      >
                        <div>
                          <div className="font-medium text-gray-900">
                            {permission.permissionName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {permission.description}
                          </div>
                        </div>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(checked) => {
                            setFormData((prev) => {
                              let updatedPermissions = [...prev.permissions];

                              const isFullAccess =
                                permission.category === 10 &&
                                permission.action === 5;

                              // ===== CASE 1: Full Access clicked =====
                              if (isFullAccess) {
                                updatedPermissions = updatedPermissions.map(
                                  (p) => ({
                                    ...p,
                                    isPermissionEnabled: checked,
                                  }),
                                );
                              } else {
                                // Update the clicked permission
                                updatedPermissions = updatedPermissions.map(
                                  (p) =>
                                    p.id === permission.id
                                      ? { ...p, isPermissionEnabled: checked }
                                      : p,
                                );
                                // 🔥 NEW LOGIC: If Upsert turned ON → auto enable View of same category
                                if (permission.action === 2 && checked) {
                                  updatedPermissions = updatedPermissions.map(
                                    (p) =>
                                      p.category === permission.category &&
                                      p.action === 1
                                        ? { ...p, isPermissionEnabled: true }
                                        : p,
                                  );
                                }
                                // 🔥 NEW LOGIC: If View turned OFF → auto disable Upsert of same category
                                if (permission.action === 1 && !checked) {
                                  updatedPermissions = updatedPermissions.map(
                                    (p) =>
                                      p.category === permission.category &&
                                      p.action === 2
                                        ? { ...p, isPermissionEnabled: false }
                                        : p,
                                  );
                                }

                                // 🔥 NEW LOGIC: If View turned OFF → auto disable Approve of same category
                                if (permission.action === 1 && !checked) {
                                  updatedPermissions = updatedPermissions.map(
                                    (p) =>
                                      p.category === permission.category &&
                                      p.action === 3
                                        ? { ...p, isPermissionEnabled: false }
                                        : p,
                                  );
                                }

                                // 🔥 NEW LOGIC: If Approve turned ON → auto enable View of same category
                                if (permission.action === 3 && checked) {
                                  updatedPermissions = updatedPermissions.map(
                                    (p) =>
                                      p.category === permission.category &&
                                      p.action === 1
                                        ? { ...p, isPermissionEnabled: true }
                                        : p,
                                  );
                                }

                                // Check if all NON full-access permissions are enabled
                                const allOthersEnabled = updatedPermissions
                                  .filter(
                                    (p) =>
                                      !(p.category === 10 && p.action === 5),
                                  )
                                  .every((p) => p.isPermissionEnabled);

                                // Update full access accordingly
                                updatedPermissions = updatedPermissions.map(
                                  (p) =>
                                    p.category === 10 && p.action === 5
                                      ? {
                                          ...p,
                                          isPermissionEnabled: allOthersEnabled,
                                        }
                                      : p,
                                );
                              }

                              return {
                                ...prev,
                                permissions: updatedPermissions,
                              };
                            });
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 justify-end pt-4 border-t border-gray-200">
        <Button variant="outline" onClick={onBack}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!formData.name.trim() || isSaving}
          className="bg-slate-900 hover:bg-slate-800"
        >
          {isSaving ? (
            <>Saving...</>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              {role
                ? isEditingSystemRole
                  ? "Save Role"
                  : "Update Role"
                : "Create Role"}
            </>
          )}
        </Button>
      </div>

      <Dialog open={showUpdateInfoModal} onOpenChange={setShowUpdateInfoModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{saveSuccessTitle}</DialogTitle>
            <DialogDescription>{saveSuccessMessage}</DialogDescription>
          </DialogHeader>

          <DialogFooter>
            <Button
              onClick={() => {
                setShowUpdateInfoModal(false);
                onBack();
              }}
              className="bg-slate-900 hover:bg-slate-800"
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
